/**
 * Validation schemas for owners, properties, contractors, tenants, tenancies and rent.
 * Money fields accept integer pence (numbers) or pounds (strings, e.g. from forms).
 */
import {
  ACCOUNTING_BASES,
  DEPOSIT_SCHEMES,
  MTD_STATUSES,
  OWNER_TYPES,
  PROPERTY_STATUSES,
  PROPERTY_TYPES,
  QUARTERLY_PERIOD_TYPES,
  RENT_CHANGE_REASONS,
  RENT_FREQUENCIES,
  TENANCY_TYPES,
  values,
} from "../constants.ts";
import { type FieldErrors, v } from "../validation.ts";

const text = (max = 5000) => v.string({ max }).default("");
const money = () => v.pence({ min: 0, fromPounds: true });

// ---------------------------------------------------------------- tax owners

export const taxOwnerSchema = v.object({
  ownerType: v.enum(values(OWNER_TYPES)),
  name: v.string({ min: 1, max: 200 }),
  email: v.email().optional(),
  utr: v
    .string({ pattern: /^\d{5}\s?\d{5}$/, patternMessage: "A UTR is 10 digits" })
    .transform((s) => s.replace(/\s/g, ""))
    .optional(),
  nino: v
    .string({
      pattern: /^[A-CEGHJ-PR-TW-Z]{2}\s?\d{2}\s?\d{2}\s?\d{2}\s?[A-D]$/i,
      patternMessage: "Enter a valid National Insurance number, e.g. QQ 12 34 56 C",
    })
    .transform((s) => s.replace(/\s/g, "").toUpperCase())
    .optional(),
  companyNumber: v
    .string({ pattern: /^[A-Z0-9]{8}$/i, patternMessage: "A company number is 8 characters" })
    .transform((s) => s.toUpperCase())
    .optional(),
  accountingBasis: v.enum(values(ACCOUNTING_BASES)),
  mtdStatus: v.enum(values(MTD_STATUSES)).default("not_signed_up"),
  mtdStartDate: v.date().optional(),
  quarterlyPeriodType: v.enum(values(QUARTERLY_PERIOD_TYPES)).default("standard"),
  consolidatedExpenses: v.boolean(),
  yearEndMonth: v.int({ min: 1, max: 12 }).optional(),
  yearEndDay: v.int({ min: 1, max: 31 }).optional(),
  notes: text(),
});

export function taxOwnerChecks(o: {
  ownerType: string;
  accountingBasis: string;
  yearEndMonth: number | null;
  yearEndDay: number | null;
}): FieldErrors | null {
  const errors: FieldErrors = {};
  if (o.ownerType === "company") {
    if (o.accountingBasis !== "accruals") errors.accountingBasis = "Companies must use the accruals basis";
    if (!o.yearEndMonth) errors.yearEndMonth = "Enter the company's accounting year end";
    if (!o.yearEndDay) errors.yearEndDay = "Enter the company's accounting year end";
  }
  return Object.keys(errors).length ? errors : null;
}

// ---------------------------------------------------------------- properties

export const propertySchema = v.object({
  reference: text(80),
  addressLine1: v.string({ min: 1, max: 200 }),
  addressLine2: text(200),
  addressLine3: text(200),
  town: v.string({ min: 1, max: 100 }),
  county: text(100),
  postcode: v.postcode(),
  country: v.string({ max: 2 }).default("GB"),
  propertyType: v.enum(values(PROPERTY_TYPES)),
  status: v.enum(values(PROPERTY_STATUSES)).default("active"),
  bedrooms: v.int({ min: 0, max: 100 }).optional(),
  bathrooms: v.int({ min: 0, max: 100 }).optional(),
  purchaseDate: v.date().optional(),
  purchasePricePence: money().optional(),
  purchaseCostsPence: money().optional(),
  currentValuePence: money().optional(),
  saleDate: v.date().optional(),
  salePricePence: money().optional(),
  saleCostsPence: money().optional(),
  description: text(),
  notes: text(),
});

export const ownershipSchema = v.object({
  shares: v.array(
    v.object({
      ownerId: v.uuid(),
      sharePercent: v.percent(),
      effectiveFromDate: v.date(),
      effectiveToDate: v.date().optional(),
    }),
    { min: 1 },
  ),
});

// ---------------------------------------------------------------- contractors

export const contractorSchema = v.object({
  name: v.string({ min: 1, max: 200 }),
  company: text(200),
  email: v.email().optional(),
  phone: text(50),
  address: text(500),
  services: v.array(v.string({ max: 60 }), { max: 30 }),
  notes: text(),
  isActive: v.boolean().default(true),
});

// ---------------------------------------------------------------- tenants & tenancies

export const tenantSchema = v.object({
  name: v.string({ min: 1, max: 200 }),
  email: v.email().optional(),
  phone: text(50),
  notes: text(),
});

export const rentPeriodSchema = v.object({
  effectiveFromDate: v.date(),
  amountPence: v.pence({ min: 0, fromPounds: true }),
  frequency: v.enum(values(RENT_FREQUENCIES)),
  dueDay: v.int({ min: 1, max: 31 }).optional(),
  reason: v.enum(values(RENT_CHANGE_REASONS)).default("initial"),
  notes: text(),
});

const tenancyFields = {
  propertyId: v.uuid(),
  reference: text(80),
  tenancyType: v.enum(values(TENANCY_TYPES)),
  startDate: v.date(),
  fixedTermEndDate: v.date().optional(),
  noticeGivenDate: v.date().optional(),
  depositPence: money().optional(),
  depositScheme: v.enum(values(DEPOSIT_SCHEMES)).optional(),
  depositReference: text(100),
  depositProtectedDate: v.date().optional(),
  depositReturnedDate: v.date().optional(),
  depositReturnedPence: money().optional(),
  depositNotes: text(),
  notes: text(),
};

const tenancyTenant = v.object({
  tenantId: v.uuid().optional(),
  // A new tenant created inline when tenantId is empty.
  name: v.string({ max: 200 }).optional(),
  email: v.email().optional(),
  phone: v.string({ max: 50 }).optional(),
  isLead: v.boolean(),
});

export const tenancySchema = v.object({
  ...tenancyFields,
  tenants: v.array(tenancyTenant, { min: 1 }),
});

export const tenancyCreateSchema = v.object({
  ...tenancyFields,
  tenants: v.array(tenancyTenant, { min: 1 }),
  // Rent is optional when creating: ignored unless an amount is entered.
  rent: v.optionalUnless(rentPeriodSchema, "amountPence"),
});

export function tenancyChecks(t: {
  startDate: string;
  fixedTermEndDate: string | null;
  tenants: Array<{ tenantId: string | null; name: string | null }>;
}): FieldErrors | null {
  const errors: FieldErrors = {};
  if (t.fixedTermEndDate && t.fixedTermEndDate < t.startDate)
    errors.fixedTermEndDate = "Fixed term end must be after the start date";
  t.tenants.forEach((tenant, i) => {
    if (!tenant.tenantId && !tenant.name) errors[`tenants.${i}.name`] = "Choose a tenant or enter a name";
  });
  return Object.keys(errors).length ? errors : null;
}

export const endTenancySchema = v.object({
  endDate: v.date(),
  endReason: text(500),
  noticeGivenDate: v.date().optional(),
});

export const rentReviewSchema = v.object({
  reviewDate: v.date(),
  proposedAmountPence: money().optional(),
  agreedAmountPence: money().optional(),
  noticeServedDate: v.date().optional(),
  effectiveFromDate: v.date().optional(),
  outcome: v.enum(["pending", "agreed", "declined", "withdrawn"] as const).default("pending"),
  notes: text(),
});
